import React from "react";
import ReactECharts from "echarts-for-react";
import ChartCard from "./ChartCard";
import { COLORS } from "../mockData";

export const DigitalAnalyticsDashboards = ({ activeTab, data, loading }) => {
  if (loading) {
    return (
      <div className="analytics-loading-placeholder">
        <div className="analytics-loading-spinner" />
        <span>Loading tab data...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="analytics-no-data">
        <p>No analytics data available for the selected filters.</p>
      </div>
    );
  }

  // --- Theme Colors ---
  const OUTCOME_COLORS = {
    remission: "#10B981", // Emerald
    improvement: "#3B82F6", // Blue
    partial: "#6366F1", // Indigo
    no_change: "#F59E0B", // Amber
    worsened: "#EF4444", // Red
  };

  const STACKED_BAR_TOOLTIP = {
    trigger: "axis",
    axisPointer: { type: "shadow" },
    formatter: (params) => {
      let html = `<strong>${params[0].name}</strong>`;
      params.forEach((p) => {
        html += `<br/>${p.marker} ${p.seriesName}: ${p.value}%`;
      });
      return html;
    },
  };

  const OUTCOME_SERIES = (sourceData, labelKey) => {
    const categories = sourceData.map((item) => item[labelKey]);
    const seriesData = {
      Remission: sourceData.map((item) => item.remission),
      Improvement: sourceData.map((item) => item.improvement),
      "Partial Improvement": sourceData.map((item) => item.partial_improvement),
      "No Change": sourceData.map((item) => item.no_change),
      Worsened: sourceData.map((item) => item.worsened),
    };

    return {
      categories,
      series: Object.keys(seriesData).map((key, idx) => ({
        name: key,
        type: "bar",
        stack: "outcomes",
        emphasis: { focus: "series" },
        itemStyle: {
          color: Object.values(OUTCOME_COLORS)[idx],
          borderRadius: idx === 4 ? [4, 4, 0, 0] : [0, 0, 0, 0],
        },
        data: seriesData[key],
      })),
    };
  };

  const getOutcomeSeriesData = (sourceData) => {
    return {
      remission: sourceData.map(item => item.remission || 0),
      improvement: sourceData.map(item => item.improvement || 0),
      partial: sourceData.map(item => item.partial_improvement || 0),
      noChangeWorsened: sourceData.map(item => (item.no_change || 0) + (item.worsened || 0))
    };
  };

  const GROUPED_BAR_OPTION = (sourceData, xKey, xAxisName, yAxisName) => {
    const categories = sourceData.map(item => item[xKey]);
    const seriesData = getOutcomeSeriesData(sourceData);

    const BAR_LABEL_STYLE = {
      show: true,
      position: "inside",
      formatter: (params) => {
        if (params.value > 0) {
          const val = Number(params.value);
          return (val % 1 === 0 ? val : val.toFixed(1)) + "%";
        }
        return "";
      },
      color: "#fff",
      fontSize: 10,
      fontWeight: "bold"
    };

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          let html = `<strong>${params[0].name}</strong>`;
          params.forEach((p) => {
            html += `<br/>${p.marker} ${p.seriesName}: ${p.value}%`;
          });
          return html;
        }
      },
      legend: { bottom: 0, data: ["Remission", "Improvement", "Partial Improvement", "No Change / Worsened"] },
      grid: { left: 60, right: 20, top: 30, bottom: 85 },
      xAxis: {
        type: "category",
        data: categories,
        name: xAxisName,
        nameLocation: "middle",
        nameGap: 30
      },
      yAxis: {
        type: "value",
        max: 100,
        axisLabel: { formatter: "{value}%" },
        name: yAxisName,
        nameLocation: "middle",
        nameGap: 40
      },
      series: [
        { name: "Remission", type: "bar", barGap: "10%", itemStyle: { color: "#3B82F6", borderRadius: [4, 4, 0, 0] }, label: BAR_LABEL_STYLE, data: seriesData.remission },
        { name: "Improvement", type: "bar", itemStyle: { color: "#10B981", borderRadius: [4, 4, 0, 0] }, label: BAR_LABEL_STYLE, data: seriesData.improvement },
        { name: "Partial Improvement", type: "bar", itemStyle: { color: "#F59E0B", borderRadius: [4, 4, 0, 0] }, label: BAR_LABEL_STYLE, data: seriesData.partial },
        { name: "No Change / Worsened", type: "bar", itemStyle: { color: "#EF4444", borderRadius: [4, 4, 0, 0] }, label: BAR_LABEL_STYLE, data: seriesData.noChangeWorsened }
      ]
    };
  };

  const STACKED_BAR_OPTION = (sourceData, xKey, xAxisName, yAxisName) => {
    const categories = sourceData.map(item => item[xKey]);
    const seriesData = getOutcomeSeriesData(sourceData);

    const BAR_LABEL_STYLE = {
      show: true,
      position: "inside",
      formatter: (params) => {
        if (params.value > 0) {
          const val = Number(params.value);
          return (val % 1 === 0 ? val : val.toFixed(1)) + "%";
        }
        return "";
      },
      color: "#fff",
      fontSize: 10,
      fontWeight: "bold"
    };

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          let html = `<strong>${params[0].name}</strong>`;
          params.forEach((p) => {
            html += `<br/>${p.marker} ${p.seriesName}: ${p.value}%`;
          });
          return html;
        }
      },
      legend: { bottom: 0, data: ["Remission", "Improvement", "Partial Improvement", "No Change / Worsened"] },
      grid: { left: 60, right: 20, top: 30, bottom: 85 },
      xAxis: {
        type: "category",
        data: categories,
        name: xAxisName,
        nameLocation: "middle",
        nameGap: 30
      },
      yAxis: {
        type: "value",
        max: 100,
        axisLabel: { formatter: "{value}%" },
        name: yAxisName,
        nameLocation: "middle",
        nameGap: 40
      },
      series: [
        { name: "Remission", type: "bar", stack: "outcomes", itemStyle: { color: "#3B82F6" }, label: BAR_LABEL_STYLE, data: seriesData.remission },
        { name: "Improvement", type: "bar", stack: "outcomes", itemStyle: { color: "#10B981" }, label: BAR_LABEL_STYLE, data: seriesData.improvement },
        { name: "Partial Improvement", type: "bar", stack: "outcomes", itemStyle: { color: "#F59E0B" }, label: BAR_LABEL_STYLE, data: seriesData.partial },
        { name: "No Change / Worsened", type: "bar", stack: "outcomes", itemStyle: { color: "#EF4444", borderRadius: [4, 4, 0, 0] }, label: BAR_LABEL_STYLE, data: seriesData.noChangeWorsened }
      ]
    };
  };

const SingleAssessmentSelector = ({ selected, onChange }) => {
  const options = [
    { id: "phq9", label: "PHQ-9" },
    { id: "gad7", label: "GAD-7" },
    { id: "promis-pain-8", label: "PROMIS Pain-8" },
    { id: "insomnia", label: "Insomnia (ISI)" }
  ];

  return (
    <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "16px", flexWrap: "wrap" }}>
      <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748B" }}>Assessment:</span>
      {options.map((opt) => {
        const isSelected = selected === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "4px 12px",
              borderRadius: "16px",
              fontSize: "12px",
              fontWeight: isSelected ? "700" : "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              border: isSelected ? "2px solid #3B82F6" : "1px solid #CBD5E1",
              background: isSelected ? "#EFF6FF" : "#FFFFFF",
              color: isSelected ? "#1D4ED8" : "#64748B",
              boxShadow: isSelected ? "0 1px 3px rgba(59, 130, 246, 0.15)" : "none"
            }}
          >
            <span style={{ fontSize: "11px", color: isSelected ? "#2563EB" : "#94A3B8" }}>
              {isSelected ? "●" : "○"}
            </span>
            <span>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
};

const ClinicalOutcomesTab = ({ data, GROUPED_BAR_OPTION, STACKED_BAR_OPTION, COLORS }) => {
  const [cohortAssessment, setCohortAssessment] = React.useState("phq9");
  const [engAssessment, setEngAssessment] = React.useState("phq9");
  const [ageAssessment, setAgeAssessment] = React.useState("phq9");
  const [genderAssessment, setGenderAssessment] = React.useState("phq9");

  const getOutcomeData = (typeKey, fieldKey, fallbackData) => {
    if (
      data.outcome_analytics_by_type &&
      data.outcome_analytics_by_type[typeKey] &&
      data.outcome_analytics_by_type[typeKey][fieldKey]
    ) {
      return data.outcome_analytics_by_type[typeKey][fieldKey];
    }
    return fallbackData || [];
  };

  // 1. Assessment Improvement Trend
  const impTrend = data.assessment_improvement_trend || [];
  const impX = impTrend.map((t) => t.month);
  const makePctTooltip = (unit = "%") => ({
    trigger: "axis",
    formatter: (params) => {
      if (!params || !params.length) return "";
      let res = `<b>${params[0].name}</b><br/>`;
      params.forEach((p) => {
        const valStr = p.value !== null && p.value !== undefined ? `${p.value}${unit}` : "No Data";
        res += `${p.marker} ${p.seriesName}: <b>${valStr}</b><br/>`;
      });
      return res;
    }
  });

  const impOption = {
    tooltip: makePctTooltip("%"),
    legend: { data: ["PHQ-9", "GAD-7", "PROMIS-Pain-8", "ISI"], bottom: 0 },
    grid: { left: 60, right: 20, top: 30, bottom: 80 },
    xAxis: {
      type: "category",
      data: impX,
      name: "Month",
      nameLocation: "middle",
      nameGap: 30
    },
    yAxis: {
      type: "value",
      axisLabel: { formatter: "{value}%" },
      name: "Improvement Rate",
      nameLocation: "middle",
      nameGap: 40
    },
    series: [
      { name: "PHQ-9", type: "line", smooth: false, connectNulls: false, data: impTrend.map((t) => t.phq9), lineStyle: { color: COLORS.phq9 }, itemStyle: { color: COLORS.phq9 } },
      { name: "GAD-7", type: "line", smooth: false, connectNulls: false, data: impTrend.map((t) => t.gad7), lineStyle: { color: COLORS.gad7 }, itemStyle: { color: COLORS.gad7 } },
      { name: "PROMIS-Pain-8", type: "line", smooth: false, connectNulls: false, data: impTrend.map((t) => t["promis-pain-8"]), lineStyle: { color: "#10B981" }, itemStyle: { color: "#10B981" } },
      { name: "ISI", type: "line", smooth: false, connectNulls: false, data: impTrend.map((t) => t.insomnia), lineStyle: { color: "#F59E0B" }, itemStyle: { color: "#F59E0B" } },
    ],
  };

  // 2. Assessment Remission Trend
  const remTrend = data.assessment_remission_trend || [];
  const remX = remTrend.map((t) => t.month);
  const remOption = {
    tooltip: makePctTooltip("%"),
    legend: { data: ["PHQ-9", "GAD-7", "PROMIS-Pain-8", "ISI"], bottom: 0 },
    grid: { left: 60, right: 20, top: 30, bottom: 80 },
    xAxis: {
      type: "category",
      data: remX,
      name: "Month",
      nameLocation: "middle",
      nameGap: 30
    },
    yAxis: {
      type: "value",
      axisLabel: { formatter: "{value}%" },
      name: "Remission Rate",
      nameLocation: "middle",
      nameGap: 40
    },
    series: [
      { name: "PHQ-9", type: "line", smooth: false, connectNulls: false, data: remTrend.map((t) => t.phq9), lineStyle: { color: COLORS.phq9 }, itemStyle: { color: COLORS.phq9 } },
      { name: "GAD-7", type: "line", smooth: false, connectNulls: false, data: remTrend.map((t) => t.gad7), lineStyle: { color: COLORS.gad7 }, itemStyle: { color: COLORS.gad7 } },
      { name: "PROMIS-Pain-8", type: "line", smooth: false, connectNulls: false, data: remTrend.map((t) => t["promis-pain-8"]), lineStyle: { color: "#10B981" }, itemStyle: { color: "#10B981" } },
      { name: "ISI", type: "line", smooth: false, connectNulls: false, data: remTrend.map((t) => t.insomnia), lineStyle: { color: "#F59E0B" }, itemStyle: { color: "#F59E0B" } },
    ],
  };

  // 3. Insomnia and Pain Severity Improvement Trend
  const avgScoreTrend = data.average_score_improvement_trend || [];
  const avgX = avgScoreTrend.map((t) => t.month);
  const avgOption = {
    tooltip: makePctTooltip(" pts"),
    legend: { data: ["PROMIS-Pain-8", "ISI"], bottom: 0 },
    grid: { left: 60, right: 20, top: 30, bottom: 80 },
    xAxis: {
      type: "category",
      data: avgX,
      name: "Month",
      nameLocation: "middle",
      nameGap: 30
    },
    yAxis: {
      type: "value",
      name: "Avg Point Reduction",
      nameLocation: "middle",
      nameGap: 40
    },
    series: [
      { name: "PROMIS-Pain-8", type: "line", smooth: false, connectNulls: false, data: avgScoreTrend.map((t) => t["promis-pain-8"]), lineStyle: { color: "#10B981" }, itemStyle: { color: "#10B981" } },
      { name: "ISI", type: "line", smooth: false, connectNulls: false, data: avgScoreTrend.map((t) => t.insomnia), lineStyle: { color: "#F59E0B" }, itemStyle: { color: "#F59E0B" } },
    ],
  };

  // 4. Outcome by Cohort (New vs Existing Patients)
  const cohortDataRaw = getOutcomeData(cohortAssessment, "outcome_by_patient_cohort", data.outcome_by_patient_cohort);
  const cohortDataMapped = cohortDataRaw.map(item => ({
    ...item,
    cohortLabel: item.cohort === "New" ? "New Patients" : "Existing Patients"
  }));
  const cohortOption = GROUPED_BAR_OPTION(cohortDataMapped, "cohortLabel", "Cohort", "Outcome Percentage");

  // 5. Outcome by Engagement Level
  const engDataRaw = getOutcomeData(engAssessment, "outcome_by_engagement_level", data.outcome_by_engagement_level);
  const engDataMapped = [...engDataRaw].sort((a, b) => {
    const order = { "Low": 1, "Medium": 2, "High": 3 };
    return (order[a.engagement] || 0) - (order[b.engagement] || 0);
  });
  const engOption = GROUPED_BAR_OPTION(engDataMapped, "engagement", "Engagement Level", "Outcome Percentage");

  // 6. Outcome by Age Group
  const ageDataRaw = getOutcomeData(ageAssessment, "outcome_by_age_group", data.outcome_by_age_group);
  const ageOption = STACKED_BAR_OPTION(ageDataRaw, "age_group", "Age Group", "Outcome Percentage");

  // 7. Outcome by Gender
  const genderDataRaw = getOutcomeData(genderAssessment, "outcome_by_gender", data.outcome_by_gender);
  const genderOption = STACKED_BAR_OPTION(genderDataRaw, "gender", "Gender", "Outcome Percentage");

  // 8. Outcome by Comorbidity
  const comorbidityDataRaw = data.outcome_by_comorbidity || [];
  const groupedComorb = {};
  comorbidityDataRaw.forEach(item => {
    const g = item.comorbidity_group;
    if (!groupedComorb[g]) {
      groupedComorb[g] = {
        comorbidity_group: g,
        remission: [],
        improvement: [],
        partial_improvement: [],
        no_change: [],
        worsened: [],
        patients: item.patients
      };
    }
    groupedComorb[g].remission.push(item.remission);
    groupedComorb[g].improvement.push(item.improvement);
    groupedComorb[g].partial_improvement.push(item.partial_improvement);
    groupedComorb[g].no_change.push(item.no_change);
    groupedComorb[g].worsened.push(item.worsened);
  });

  const averagedComorbList = Object.values(groupedComorb).map(item => {
    const avg = (arr) => arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length * 10) / 10 : 0;
    return {
      comorbidity_group: item.comorbidity_group,
      remission: avg(item.remission),
      improvement: avg(item.improvement),
      partial_improvement: avg(item.partial_improvement),
      no_change: avg(item.no_change),
      worsened: avg(item.worsened),
      patients: item.patients
    };
  });
  const comorbOption = GROUPED_BAR_OPTION(averagedComorbList, "comorbidity_group", "Comorbidity Group", "Outcome Percentage");

  // 9. Patient Distribution by Comorbidity Group (Horizontal Bar Chart)
  const uniqueGroups = averagedComorbList.map(item => item.comorbidity_group);
  const patientCounts = averagedComorbList.map(item => item.patients);
  const distOption = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (params) => `${params[0].name}: ${params[0].value} patients`
    },
    grid: { left: 180, right: 40, top: 30, bottom: 65 },
    xAxis: {
      type: "value",
      name: "Number of Patients",
      nameLocation: "middle",
      nameGap: 30
    },
    yAxis: {
      type: "category",
      data: uniqueGroups,
      name: "Comorbidity Group",
      nameLocation: "middle",
      nameGap: 150,
      axisLabel: { interval: 0 }
    },
    series: [
      {
        name: "Patients",
        type: "bar",
        data: patientCounts,
        label: {
          show: true,
          position: "inside",
          formatter: "{c}",
          color: "#fff",
          fontSize: 10,
          fontWeight: "bold"
        },
        itemStyle: {
          color: "#3B82F6",
          borderRadius: [0, 4, 4, 0]
        }
      }
    ]
  };

  // 10. Comorbidity View Table
  const comorbidities = data.outcome_by_comorbidity || [];

  return (
    <div className="phase2-tab-content">
      {/* Trend line charts in a 2-column grid */}
      <div className="analytics-charts-grid">
        <ChartCard title="Assessment Improvement Trend">
          <ReactECharts key="clinical-outcomes-imp" option={impOption} style={{ height: 320 }} notMerge={true} />
        </ChartCard>
        <ChartCard title="Assessment Remission Trend">
          <ReactECharts key="clinical-outcomes-rem" option={remOption} style={{ height: 320 }} notMerge={true} />
        </ChartCard>
        <ChartCard title="Insomnia and Pain Severity Improvement Trend">
          <ReactECharts key="clinical-outcomes-avg" option={avgOption} style={{ height: 320 }} notMerge={true} />
        </ChartCard>
      </div>

      {/* Grouped and Stacked Bar charts - rendered full-width (stacked vertically) */}
      <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 24 }}>
        <ChartCard title="Outcome by Cohort (New vs Existing Patients)">
          <SingleAssessmentSelector selected={cohortAssessment} onChange={setCohortAssessment} />
          <ReactECharts key={`clinical-outcomes-cohort-${cohortAssessment}`} option={cohortOption} style={{ height: 360 }} notMerge={true} />
        </ChartCard>
        
        <ChartCard title="Outcome by Engagement Level">
          <SingleAssessmentSelector selected={engAssessment} onChange={setEngAssessment} />
          <ReactECharts key={`clinical-outcomes-eng-${engAssessment}`} option={engOption} style={{ height: 360 }} notMerge={true} />
        </ChartCard>

        <ChartCard title="Outcome by Age Group">
          <SingleAssessmentSelector selected={ageAssessment} onChange={setAgeAssessment} />
          <ReactECharts key={`clinical-outcomes-age-${ageAssessment}`} option={ageOption} style={{ height: 360 }} notMerge={true} />
        </ChartCard>

        <ChartCard title="Outcome by Gender">
          <SingleAssessmentSelector selected={genderAssessment} onChange={setGenderAssessment} />
          <ReactECharts key={`clinical-outcomes-gender-${genderAssessment}`} option={genderOption} style={{ height: 360 }} notMerge={true} />
        </ChartCard>

        <ChartCard title="Outcome by Comorbidity">
          <ReactECharts key="clinical-outcomes-comorb" option={comorbOption} style={{ height: 360 }} notMerge={true} />
        </ChartCard>

        <ChartCard title="Patient Distribution by Comorbidity Group">
          <ReactECharts key="clinical-outcomes-dist" option={distOption} style={{ height: 360 }} notMerge={true} />
        </ChartCard>

        <ChartCard title="Population Health: Outcome by Comorbidity">
          <div className="analytics-table-wrapper" style={{ overflowX: "auto" }}>
            <table className="analytics-custom-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#F1F5F9", textAlign: "left" }}>
                  <th style={{ padding: 12, borderBottom: "1px solid #E2E8F0" }}>Comorbidity Group</th>
                  <th style={{ padding: 12, borderBottom: "1px solid #E2E8F0" }}>Assessment Type</th>
                  <th style={{ padding: 12, borderBottom: "1px solid #E2E8F0" }}>Total Patients</th>
                  <th style={{ padding: 12, borderBottom: "1px solid #E2E8F0" }}>Remission %</th>
                  <th style={{ padding: 12, borderBottom: "1px solid #E2E8F0" }}>Improvement %</th>
                  <th style={{ padding: 12, borderBottom: "1px solid #E2E8F0" }}>Partial Improvement %</th>
                  <th style={{ padding: 12, borderBottom: "1px solid #E2E8F0" }}>No Change %</th>
                  <th style={{ padding: 12, borderBottom: "1px solid #E2E8F0" }}>Worsened %</th>
                </tr>
              </thead>
              <tbody>
                {comorbidities.map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #F1F5F9" }}>
                    <td style={{ padding: 12 }}>{row.comorbidity_group}</td>
                    <td style={{ padding: 12 }}>{row.assessment_type}</td>
                    <td style={{ padding: 12 }}>{row.patients}</td>
                    <td style={{ padding: 12, color: "#3B82F6", fontWeight: 600 }}>{row.remission}%</td>
                    <td style={{ padding: 12, color: "#10B981", fontWeight: 600 }}>{row.improvement}%</td>
                    <td style={{ padding: 12, color: "#F59E0B", fontWeight: 600 }}>{row.partial_improvement}%</td>
                    <td style={{ padding: 12, color: "#64748B" }}>{row.no_change}%</td>
                    <td style={{ padding: 12, color: "#EF4444" }}>{row.worsened}%</td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </ChartCard>
        </div>
      </div>
    );
  };

  // ==========================================
  // TAB 2: PATIENT ENGAGEMENT
  // ==========================================
  const renderPatientEngagement = () => {
    const act = data.active_users || {};
    const dauData = Array.isArray(act.dau) ? act.dau : [];
    const wauData = Array.isArray(act.wau) ? act.wau : [];
    const mauData = Array.isArray(act.mau) ? act.mau : [];
    const usage = data.feature_usage || [];

    // 1. DAU Trend Line
    const dauOption = {
      tooltip: { trigger: "axis" },
      grid: { left: 60, right: 20, top: 20, bottom: 50 },
      xAxis: {
        type: "category",
        data: dauData.map((d) => d.date),
        name: "Date",
        nameLocation: "middle",
        nameGap: 30
      },
      yAxis: {
        type: "value",
        name: "Active Users",
        nameLocation: "middle",
        nameGap: 40
      },
      series: [
        {
          name: "Daily Active Users",
          type: "line",
          smooth: false,
          showSymbol: true,
          data: dauData.map((d) => d.count),
          lineStyle: { color: COLORS.gad7, width: 2 },
          itemStyle: { color: COLORS.gad7 },
        },
      ],
    };

    // 2. WAU Trend Line
    const wauOption = {
      tooltip: { trigger: "axis" },
      grid: { left: 60, right: 20, top: 20, bottom: 50 },
      xAxis: {
        type: "category",
        data: wauData.map((w) => w.week),
        name: "Week",
        nameLocation: "middle",
        nameGap: 30
      },
      yAxis: {
        type: "value",
        name: "Active Users",
        nameLocation: "middle",
        nameGap: 40
      },
      series: [
        {
          name: "Weekly Active Users",
          type: "line",
          smooth: false,
          showSymbol: true,
          data: wauData.map((w) => w.count),
          lineStyle: { color: COLORS.phq9, width: 2 },
          itemStyle: { color: COLORS.phq9 },
        },
      ],
    };

    // 3. MAU Trend Line
    const mauOption = {
      tooltip: { trigger: "axis" },
      grid: { left: 60, right: 20, top: 20, bottom: 50 },
      xAxis: {
        type: "category",
        data: mauData.map((m) => m.month),
        name: "Month",
        nameLocation: "middle",
        nameGap: 30
      },
      yAxis: {
        type: "value",
        name: "Active Users",
        nameLocation: "middle",
        nameGap: 40
      },
      series: [
        {
          name: "Monthly Active Users",
          type: "line",
          smooth: false,
          showSymbol: true,
          data: mauData.map((m) => m.count),
          lineStyle: { color: COLORS.primary, width: 3 },
          itemStyle: { color: COLORS.primary },
        },
      ],
    };

    // 4. Feature Usage - Horizontal Bar Chart (Percentage of unique active patients)
    const usageCategories = [
      "Profile",
      "Assessments",
      "Journal",
      "Check-ins",
      "Chat",
      "Notification Settings",
    ];

    const getCountForCategory = (catName) => {
      let backendKey = catName;
      if (catName === "Journal") backendKey = "Journal Entries";
      else if (catName === "Chat") backendKey = "Chat Messages";
      else if (catName === "Notification Settings") backendKey = "Notification Updates";
      else if (catName === "Profile") backendKey = "Profile Updates";

      const item = usage.find((u) => u.feature.toLowerCase() === backendKey.toLowerCase());
      return item ? item.count : 0;
    };

    const activePatientsCount = data.active_vs_inactive?.active_count || 0;

    const categoryData = usageCategories.map((catName) => {
      const count = getCountForCategory(catName);
      const pct = activePatientsCount > 0 ? (count / activePatientsCount) * 100 : 0;
      return {
        name: catName,
        count: count,
        pct: Number(pct.toFixed(1)),
      };
    });

    // Sort in descending order of usage percentage (highest at the top)
    categoryData.sort((a, b) => b.pct - a.pct);

    const usageOption = {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const item = categoryData.find((c) => c.name === params[0].name);
          return `<strong>${params[0].name}</strong><br/>` +
                 `Usage: ${item ? item.pct : 0}% (${item ? item.count : 0} of ${activePatientsCount} active patients)`;
        }
      },
      grid: { left: 140, right: 40, top: 20, bottom: 50 },
      xAxis: {
        type: "value",
        name: "Feature Usage (%)",
        nameLocation: "middle",
        nameGap: 30,
        min: 0,
        max: 100,
        axisLabel: {
          formatter: "{value}%"
        }
      },
      yAxis: {
        type: "category",
        data: categoryData.map((c) => c.name),
        name: "Features",
        nameLocation: "end",
        nameGap: 10,
        inverse: true,
        axisLabel: {
          interval: 0
        }
      },
      series: [
        {
          name: "Feature Usage (%)",
          type: "bar",
          data: categoryData.map((c) => c.pct),
          label: {
            show: true,
            position: "right",
            color: "#374151",
            fontWeight: "bold",
            fontSize: 11,
            formatter: "{c}%"
          },
          itemStyle: {
            color: "#B0D8F0",
            borderRadius: [0, 4, 4, 0],
          },
        },
      ],
    };

    // 5. Correlation Scatter Plot with Regression line
    const corr = data.correlation_engagement_vs_outcomes || { scatter_data: [], pearson_r: 0 };
    const rawScatterData = corr.scatter_data || [];
    const allScatterPoints = rawScatterData.map((s) => [s.engagement_score, s.outcome_improvement, s.patient_id, s.type_label || "All"]);

    const typeColorMap = {
      "PHQ-9": "#845EF4",
      "GAD-7": "#0B92FE",
      "Insomnia (ISI)": "#10B981",
      "PROMIS Pain-8": "#F59E0B"
    };

    const scatterGrouped = {};
    rawScatterData.forEach((s) => {
      const label = s.type_label || "Assessments";
      if (!scatterGrouped[label]) scatterGrouped[label] = [];
      scatterGrouped[label].push([s.engagement_score, s.outcome_improvement, s.patient_id, label]);
    });

    const scatterSeriesList = Object.keys(scatterGrouped).map((label) => ({
      name: label,
      type: "scatter",
      symbolSize: 10,
      itemStyle: { color: typeColorMap[label] || "#3B82F6", opacity: 0.8 },
      data: scatterGrouped[label]
    }));
    
    let regressionSeries = [];
    if (allScatterPoints.length >= 2) {
      const xVals = allScatterPoints.map((p) => p[0]);
      const yVals = allScatterPoints.map((p) => p[1]);
      const n = allScatterPoints.length;
      const sumX = xVals.reduce((a, b) => a + b, 0);
      const sumY = yVals.reduce((a, b) => a + b, 0);
      const sumXY = allScatterPoints.reduce((sum, p) => sum + p[0] * p[1], 0);
      const sumXX = xVals.reduce((sum, x) => sum + x * x, 0);
      
      const mSlope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX || 1);
      const cIntercept = (sumY - mSlope * sumX) / n;
      
      const minX = Math.min(...xVals);
      const maxX = Math.max(...xVals);
      
      regressionSeries = [
        {
          name: "Best Fit Line",
          type: "line",
          showSymbol: false,
          data: [
            [minX, mSlope * minX + cIntercept],
            [maxX, mSlope * maxX + cIntercept],
          ],
          lineStyle: { color: COLORS.gad7, width: 2, type: "solid" },
        }
      ];
    }

    const scatterOption = {
      title: { text: `Pearson r = ${corr.pearson_r}`, right: 10, top: 0, textStyle: { fontSize: 13, color: COLORS.body } },
      legend: { bottom: 0, left: "center" },
      tooltip: {
        formatter: (params) => {
          if (params.seriesType === "line") return "Trend Line";
          const labels = { 0: "0 (No Improvement)", 1: "1 (Partial Improvement)", 2: "2 (Improved)", 3: "3 (Remission)" };
          const outcomeText = labels[params.value[1]] !== undefined ? labels[params.value[1]] : params.value[1];
          return `Patient ID: ${params.value[2]}<br/>Assessment: ${params.value[3]}<br/>Engagement Score: ${params.value[0]}<br/>Outcome Score: ${outcomeText}`;
        },
      },
      grid: { left: 110, right: 30, top: 40, bottom: 70 },
      xAxis: {
        name: "Engagement Score",
        type: "value",
        nameLocation: "middle",
        nameGap: 30,
        splitLine: { lineStyle: { type: "dashed" } }
      },
      yAxis: {
        name: "Outcome Score (0-3)",
        type: "value",
        min: 0,
        max: 3,
        interval: 1,
        nameLocation: "middle",
        nameGap: 80,
        splitLine: { lineStyle: { type: "dashed" } },
        axisLabel: {
          formatter: (val) => {
            const labels = { 0: "0 (None)", 1: "1 (Partial)", 2: "2 (Improved)", 3: "3 (Remission)" };
            return labels[val] !== undefined ? labels[val] : val;
          }
        }
      },
      series: [
        ...scatterSeriesList,
        ...regressionSeries,
      ],
    };

    // 6. App Retention
    const ret = data.user_retention || [];
    const retOption = {
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          if (!params || !params.length) return "";
          const item = params[0];
          const val = item.value;
          return `${item.name}: ${val !== null && val !== undefined ? val + "%" : "N/A (Milestone in progress)"}`;
        }
      },
      legend: { data: ["Retention %"], top: 0, right: 10 },
      grid: { left: 60, right: 20, top: 30, bottom: 50 },
      xAxis: {
        type: "category",
        data: ret.map((r) => r.milestone),
        name: "Retention Period",
        nameLocation: "middle",
        nameGap: 30
      },
      yAxis: {
        type: "value",
        axisLabel: { formatter: "{value}%" },
        max: 100,
        name: "Percentage of Users",
        nameLocation: "middle",
        nameGap: 40
      },
      series: [
        {
          name: "Retention %",
          type: "line",
          smooth: false,
          data: ret.map((r) => r.rate),
          lineStyle: { color: COLORS.gad7, width: 3 },
          itemStyle: { color: COLORS.gad7 },
          areaStyle: {
            color: {
              type: "linear",
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(11, 146, 254, 0.25)" },
                { offset: 1, color: "rgba(11, 146, 254, 0.0)" },
              ],
            },
          },
        },
      ],
    };

    // 7. Active vs Inactive Patients - Pie Chart with absolute counts + percentage labels
    const activeData = data.active_vs_inactive || { active_count: 0, active_pct: 0, inactive_count: 0, inactive_pct: 0 };
    const activeOption = {
      tooltip: { trigger: "item", formatter: "{b}: {c} patients ({d}%)" },
      legend: { bottom: 0, data: ["Active patients", "Inactive patients"] },
      series: [
        {
          type: "pie",
          radius: "65%",
          center: ["50%", "45%"],
          avoidLabelOverlap: true,
          label: {
            show: true,
            position: "outside",
            formatter: "{b}\n{c} ({d}%)",
            fontSize: 12,
            fontWeight: "bold",
          },
          labelLine: { show: true },
          data: [
            { value: activeData.active_count, name: "Active patients", itemStyle: { color: "#10B981" } },
            { value: activeData.inactive_count, name: "Inactive patients", itemStyle: { color: "#EF4444" } },
          ],
        },
      ],
    };

    return (
      <div className="phase2-tab-content">
        <div className="analytics-charts-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <ChartCard title="Daily Active Users (DAU)">
            <ReactECharts key="patient-engagement-dau" option={dauOption} style={{ height: 320 }} notMerge={true} />
          </ChartCard>
          <ChartCard title="Weekly Active Users (WAU)">
            <ReactECharts key="patient-engagement-wau" option={wauOption} style={{ height: 320 }} notMerge={true} />
          </ChartCard>
        </div>

        <div style={{ marginTop: 24 }}>
          <ChartCard title="Monthly Active Users (MAU)">
            <ReactECharts key="patient-engagement-mau" option={mauOption} style={{ height: 320 }} notMerge={true} />
          </ChartCard>
        </div>

        <div style={{ marginTop: 24 }}>
          <ChartCard title="Feature Usage">
            <ReactECharts key="patient-engagement-usage" option={usageOption} style={{ height: 360 }} notMerge={true} />
          </ChartCard>
        </div>

        <div className="analytics-charts-grid" style={{ marginTop: 24 }}>
          <ChartCard title="Correlation: Engagement vs Outcomes">
            <ReactECharts key="patient-engagement-scatter" option={scatterOption} style={{ height: 360 }} notMerge={true} />
          </ChartCard>
          <ChartCard title="App Retention (7 days, 30 days, 60 days, 90 days)">
            <ReactECharts key="patient-engagement-ret" option={retOption} style={{ height: 360 }} notMerge={true} />
          </ChartCard>
        </div>

        <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "1fr", gap: 24 }}>
          <ChartCard title="Active vs Inactive Patients">
            <ReactECharts key="patient-engagement-active" option={activeOption} style={{ height: 360 }} notMerge={true} />
          </ChartCard>
        </div>
      </div>
    );
  };

  // ==========================================
  // TAB 3: ASSESSMENT ANALYTICS
  // ==========================================
  const renderAssessmentAnalytics = () => {
    const typeMapping = {
      "phq9": "PHQ-9",
      "gad7": "GAD-7",
      "insomnia": "Insomnia",
      "promis-pain-8": "PROMIS 8a"
    };

    // 1. Completion Rate: Grouped vertical bars (Expected vs Completed)
    const comp = data.assessment_completion_rate || [];
    const compX = comp.map(c => typeMapping[c.assessment_type] || c.assessment_type);
    const expectedData = comp.map(c => c.expected);
    const completedData = comp.map(c => c.completed);

    const compOption = {
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      legend: { bottom: 0, data: ["Expected Assessments", "Completed Assessments"] },
      grid: { left: 60, right: 20, top: 30, bottom: 85 },
      xAxis: {
        type: "category",
        data: compX,
        name: "Assessment Type",
        nameLocation: "middle",
        nameGap: 30
      },
      yAxis: {
        type: "value",
        name: "Number of Assessments",
        nameLocation: "middle",
        nameGap: 40
      },
      series: [
        {
          name: "Expected Assessments",
          type: "bar",
          data: expectedData,
          itemStyle: { color: "#3B82F6", borderRadius: [4, 4, 0, 0] },
          label: { show: true, position: "inside", color: "#fff", fontWeight: "bold" }
        },
        {
          name: "Completed Assessments",
          type: "bar",
          data: completedData,
          itemStyle: { color: "#8B5CF6", borderRadius: [4, 4, 0, 0] },
          label: { show: true, position: "inside", color: "#fff", fontWeight: "bold" }
        }
      ]
    };

    // 2. Average Time Between Assessments (Vertical bar chart)
    const gap = data.average_time_between_assessments || [];
    const gapX = gap.map(g => typeMapping[g.assessment_type] || g.assessment_type);
    const gapOption = {
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, formatter: "{b}: {c} days" },
      grid: { left: 60, right: 20, top: 30, bottom: 85 },
      xAxis: {
        type: "category",
        data: gapX,
        name: "Assessment Type",
        nameLocation: "middle",
        nameGap: 30
      },
      yAxis: {
        type: "value",
        name: "Average Days Between Assessments",
        nameLocation: "middle",
        nameGap: 40
      },
      series: [
        {
          name: "Average Gap (Days)",
          type: "bar",
          data: gap.map(g => g.avg_days),
          itemStyle: { color: "#3B82F6", borderRadius: [4, 4, 0, 0] },
          label: {
            show: true,
            position: "inside",
            color: "#fff",
            fontWeight: "bold",
            formatter: (params) => {
              if (params.value > 0) {
                const val = Number(params.value);
                return (val % 1 === 0 ? val : val.toFixed(1));
              }
              return "";
            }
          }
        }
      ]
    };

    // 3. Patients Overdue For Reassessment: Grouped vertical bars (duration by type)
    const overdue = data.assessment_overdue_by_type || [];
    const overdueX = overdue.map(o => typeMapping[o.assessment_type] || o.assessment_type);
    const overdueOption = {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          let html = `<strong>${params[0].name}</strong>`;
          params.forEach((p) => {
            html += `<br/>${p.marker} ${p.seriesName}: ${p.value} patients`;
          });
          return html;
        }
      },
      legend: { bottom: 0, data: ["1-7 days", "8-14 days", "15-30 days", ">30 days"] },
      grid: { left: 60, right: 20, top: 30, bottom: 85 },
      xAxis: {
        type: "category",
        data: overdueX,
        name: "Assessment Type",
        nameLocation: "middle",
        nameGap: 30
      },
      yAxis: {
        type: "value",
        name: "Number of Patients",
        nameLocation: "middle",
        nameGap: 40
      },
      series: [
        { name: "1-7 days", type: "bar", data: overdue.map(o => o.dur_1_7), itemStyle: { color: "#3B82F6", borderRadius: [4, 4, 0, 0] }, label: { show: true, position: "inside", color: "#fff", fontWeight: "bold" } },
        { name: "8-14 days", type: "bar", data: overdue.map(o => o.dur_8_14), itemStyle: { color: "#10B981", borderRadius: [4, 4, 0, 0] }, label: { show: true, position: "inside", color: "#fff", fontWeight: "bold" } },
        { name: "15-30 days", type: "bar", data: overdue.map(o => o.dur_15_30), itemStyle: { color: "#F59E0B", borderRadius: [4, 4, 0, 0] }, label: { show: true, position: "inside", color: "#fff", fontWeight: "bold" } },
        { name: ">30 days", type: "bar", data: overdue.map(o => o.dur_gt_30), itemStyle: { color: "#EF4444", borderRadius: [4, 4, 0, 0] }, label: { show: true, position: "inside", color: "#fff", fontWeight: "bold" } }
      ]
    };

    // 4. Time to First Assessment (Percentages and updated buckets)
    const first = data.time_to_first_assessment || [];
    const firstX = first.map(f => f.label);
    const firstPctData = first.map(f => f.pct);
    const firstOption = {
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, formatter: "{b}: {c}%" },
      grid: { left: 60, right: 20, top: 30, bottom: 85 },
      xAxis: {
        type: "category",
        data: firstX,
        name: "Time To First Assessment",
        nameLocation: "middle",
        nameGap: 45,
        axisLabel: {
          interval: 0,
          rotate: 15,
          fontSize: 11
        }
      },
      yAxis: {
        type: "value",
        name: "Percentage of Users",
        nameLocation: "middle",
        nameGap: 40,
        axisLabel: { formatter: "{value}%" }
      },
      series: [
        {
          name: "Percentage of Users",
          type: "bar",
          data: firstPctData,
          itemStyle: { color: "#3B82F6", borderRadius: [4, 4, 0, 0] },
          label: {
            show: true,
            position: "inside",
            color: "#fff",
            fontWeight: "bold",
            formatter: (params) => {
              if (params.value > 0) {
                const val = Number(params.value);
                return (val % 1 === 0 ? val : val.toFixed(1)) + "%";
              }
              return "";
            }
          }
        }
      ]
    };

    return (
      <div className="phase2-tab-content">
        <div className="analytics-charts-grid">
          <ChartCard title="Assessment Completion Rate">
            <ReactECharts key="assessment-analytics-comp" option={compOption} style={{ height: 320 }} notMerge={true} />
          </ChartCard>
          <ChartCard title="Average Time Between Assessments">
            <ReactECharts key="assessment-analytics-gap" option={gapOption} style={{ height: 320 }} notMerge={true} />
          </ChartCard>
        </div>

        <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          <ChartCard title="Patients Overdue For Reassessment">
            <ReactECharts key="assessment-analytics-overdue" option={overdueOption} style={{ height: 360 }} notMerge={true} />
          </ChartCard>

          <div className="analytics-charts-grid">
            <ChartCard title="Time To First Assessment">
              <ReactECharts key="assessment-analytics-first" option={firstOption} style={{ height: 320 }} notMerge={true} />
            </ChartCard>
          </div>
        </div>
      </div>
    );
  };

  // ==========================================
  // TAB 4: PATIENT ONBOARDING AND ACTIVATION
  // ==========================================
  const renderOnboardingActivation = () => {
    // 1. Onboarding Rate Trend
    const onb = data.onboarding_rate || [];
    const onbOption = {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        formatter: (params) => {
          if (!params || !params.length) return "";
          let html = `<strong>${params[0].name}</strong>`;
          params.forEach((p) => {
            const valStr = p.seriesName.includes("Rate") ? `${p.value}%` : `${p.value} patients`;
            html += `<br/>${p.marker} ${p.seriesName}: <b>${valStr}</b>`;
          });
          return html;
        }
      },
      legend: {
        data: ["Monthly Registrations", "Onboarding Rate (%)"],
        bottom: 0
      },
      grid: { left: 60, right: 65, top: 35, bottom: 75 },
      xAxis: {
        type: "category",
        data: onb.map((o) => o.month),
        name: "Month",
        nameLocation: "middle",
        nameGap: 30
      },
      yAxis: [
        {
          type: "value",
          name: "Monthly Registrations",
          nameLocation: "middle",
          nameGap: 40,
          minInterval: 1,
          axisLine: { show: true },
          splitLine: { show: true, lineStyle: { type: "dashed" } }
        },
        {
          type: "value",
          name: "Onboarding Rate (%)",
          nameLocation: "middle",
          nameGap: 40,
          min: 0,
          max: 100,
          axisLabel: { formatter: "{value}%" },
          splitLine: { show: false }
        }
      ],
      series: [
        {
          name: "Monthly Registrations",
          type: "line",
          yAxisIndex: 0,
          smooth: false,
          data: onb.map((o) => o.registered_count || 0),
          lineStyle: { color: "#60A5FA", width: 3 },
          itemStyle: { color: "#60A5FA" },
          label: {
            show: true,
            position: "top",
            formatter: "{c}",
            fontWeight: "bold",
            color: "#1E293B"
          }
        },
        {
          name: "Onboarding Rate (%)",
          type: "line",
          yAxisIndex: 1,
          smooth: false,
          data: onb.map((o) => o.rate),
          lineStyle: { color: "#1D4ED8", width: 3 },
          itemStyle: { color: "#1D4ED8" }
        }
      ]
    };

    // 2. Drop-off Funnel Chart (3 stages)
    const drop = data.drop_off_rate || [];
    const hasDropData = drop && drop.length > 0 && drop.some(d => d.count > 0);
    const dropOption = {
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          return `${params.data.stepName}: ${params.data.value} patients`;
        }
      },
      legend: { bottom: 0 },
      series: [
        {
          name: "Drop Off Funnel",
          type: "funnel",
          left: "10%",
          right: "10%",
          top: "10%",
          bottom: "15%",
          width: "80%",
          min: 0,
          maxSize: "100%",
          sort: "descending",
          gap: 6,
          label: {
            show: true,
            position: "inside",
            formatter: "{b}",
            textStyle: { color: "#FFF", fontSize: 12, fontWeight: "bold" },
          },
          labelLine: { show: false },
          itemStyle: {
            borderColor: "#FFF",
            borderWidth: 2,
            shadowBlur: 5,
            shadowColor: "rgba(0, 0, 0, 0.1)",
          },
          data: drop.map((d, idx) => ({
            value: d.count,
            stepName: d.step,
            name: `${d.step}\n${d.count} (${d.pct}%)`,
            itemStyle: {
              color: ["#0B92FE", "#10B981", "#EF4444"][idx] || "#DBEAFE",
            },
          })),
        },
      ],
    };

    return (
      <div className="phase2-tab-content">
        <div className="analytics-charts-grid">
          <ChartCard title="New Patient Onboarding Rate">
            <ReactECharts key="onboarding-activation-onb" option={onbOption} style={{ height: 320 }} notMerge={true} />
          </ChartCard>
          <ChartCard title="Drop Off Rate After Onboarding (7 days)">
            {hasDropData ? (
              <>
                <ReactECharts key="onboarding-activation-drop" option={dropOption} style={{ height: 320 }} notMerge={true} />
                {data.pending_onboarding_count > 0 && (
                  <div style={{
                    marginTop: 10,
                    padding: "8px 12px",
                    backgroundColor: "#F0F9FF",
                    borderLeft: "4px solid #0284C7",
                    borderRadius: "4px",
                    fontSize: "12px",
                    color: "#0369A1",
                    lineHeight: "1.4"
                  }}>
                    <strong>Note:</strong> {data.pending_onboarding_count} patient(s) are still within their 7-day onboarding window and are not yet eligible for drop-off evaluation. They will be classified as either Onboarded or Dropped Off once their onboarding window has ended.
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: "flex", height: 320, alignItems: "center", justifyContent: "center", color: "#64748B", fontWeight: 500, fontSize: 14 }}>
                No Data Available
              </div>
            )}
          </ChartCard>
        </div>
      </div>
    );
  };

  switch (activeTab) {
    case "clinical_outcomes":
      return <ClinicalOutcomesTab data={data} GROUPED_BAR_OPTION={GROUPED_BAR_OPTION} STACKED_BAR_OPTION={STACKED_BAR_OPTION} COLORS={COLORS} />;
    case "patient_engagement":
      return renderPatientEngagement();
    case "assessment_analytics":
      return renderAssessmentAnalytics();
    case "onboarding_activation":
      return renderOnboardingActivation();
    default:
      return null;
  }
};
